/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { usePostHog } from '@posthog/react';
import { errorAlert, successAlert, warningAlert } from 'utils/alert';

import { buttonStyle } from 'components/buttons/buttonStyle';
import MenuButton, { MenuButtonItem } from 'components/buttons/MenuButton';
import ListCreateButton, { NEW_DESIGN_CREATE_CLASS } from 'components/buttons/ListCreateButton';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import Loader from 'components/Loader';
import DateRangePickerModal from 'components/modals/date-range/DateRangePickerModal';
import AddNewCreditInvoiceFormModal, {
    AddNewCreditInvoiceForm,
} from 'components/modals/invoices/AddNewCreditInvoiceFormModal';
import AddNewPrepaidInvoiceFormModal, {
    AddNewPrepaidInvoiceForm,
} from 'components/modals/invoices/AddNewPrepaidInvoiceFormModal';
import { useModal } from 'components/modals/useModal';
import PageHeader from 'components/PageHeader';
import PermissionGuard from 'components/security/PermissionGuard';
import { Button } from 'components/ui/button';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'store';
import {
    useCreateBlankCreditNoteMutation,
    useCreateDraftInvoicesMutation,
    useCreateJointInvoiceMutation,
    useCreatePrepaidInvoiceMutation,
} from 'store/api/invoices';
import { ERROR_CODE_MESSAGES } from 'store/api/portalAPI';
import { BillableMatterListItem } from 'types/billable-matters';
import { FeatureFlag } from 'types/features';
import { CreateDraftInvoicesDto, ErroredMatterDto, MatterAmountDto } from 'types/invoices';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import BillableMattersTable from './BillableMattersTable';

const BillableMattersPage = () => {
    const posthog = usePostHog();
    const isNewDesign = useIsNewDesign();
    const [selectedMatters, setSelectedMatters] = useState(new Set<number>());
    const [excludedFromJointInvoicing, setExcludedFromJointInvoicing] = useState<BillableMatterListItem[]>([]);
    const [createDrafts, { isLoading: isLoadingDraftInvoiceCreation }] = useCreateDraftInvoicesMutation();
    const [createJointInvoice, { isLoading: isLoadingJointInvoiceCreation }] = useCreateJointInvoiceMutation();
    const [createPrepaid] = useCreatePrepaidInvoiceMutation();
    const [createBlankCreditNote] = useCreateBlankCreditNoteMutation();
    const selectedMattersWithJointInvoiceDisabled = useMemo(
        () => excludedFromJointInvoicing.filter((item) => Array.from(selectedMatters).includes(item.id)),
        [selectedMatters, excludedFromJointInvoicing]
    );

    const navigate = useNavigate();
    const { openModal, closeModal } = useModal();

    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const isDateRangeEnabled = useIsFeatureEnabled(FeatureFlag.PRESELECT_TC_EXP_MANUAL_INVOICE);

    const handleAddNewPrepaidInvoice = useCallback(
        async (formData: AddNewPrepaidInvoiceForm) => {
            const matterId = formData.matter?.id;
            if (!matterId) {
                errorAlert(t('forms.error-submitting-the-form'));
                return;
            }

            const body: MatterAmountDto = {
                matterId,
                amount: formData.prepaymentSum ?? '0.00',
            };

            await createPrepaid({ ...body })
                .unwrap()
                .then((resp) => {
                    posthog.capture('invoice_created', { type: 'prepaid' });
                    if (resp.id) navigate(`/invoice/${resp.id}`);
                })
                .catch(console.error);
        },
        [createPrepaid, navigate, posthog]
    );

    const openAddNewPrepaidInvoiceModal = useCallback((): void => {
        openModal(
            <AddNewPrepaidInvoiceFormModal
                onSubmit={(data) => {
                    void handleAddNewPrepaidInvoice(data);
                }}
                onClose={closeModal}
            />,
            closeModal
        );
    }, [closeModal, handleAddNewPrepaidInvoice, openModal]);

    const handleAddNewCreditInvoice = useCallback(
        async (formData: AddNewCreditInvoiceForm) => {
            const dtoList: MatterAmountDto[] = [
                ...formData.inProgress,
                ...formData.pending,
                ...formData.completed,
                ...formData.offerWon,
                ...formData.offerFailed,
            ]
                .filter((el) => el.selected)
                .map((el) => ({ matterId: el.id, amount: el.amount || '0.00' }));

            if (dtoList.length) {
                await createBlankCreditNote({ dtoList })
                    .unwrap()
                    .then((resp) => {
                        posthog.capture('invoice_created', { type: 'blank_credit_note' });
                        if (resp.id) navigate(`/invoice/${resp.id}`);
                    })
                    .catch(console.error);
            }
        },
        [createBlankCreditNote, navigate, posthog]
    );

    const openAddNewCreditInvoiceModal = useCallback((): void => {
        openModal(
            <AddNewCreditInvoiceFormModal
                onSubmit={(data) => {
                    void handleAddNewCreditInvoice(data);
                }}
                onClose={closeModal}
            />,
            closeModal,
            { width: 900 }
        );
    }, [closeModal, handleAddNewCreditInvoice, openModal]);

    const handleMatterSelect = (id: number, selected: boolean) => {
        setSelectedMatters((previousValue) => {
            const copyOfSelectedMatters = new Set(Array.from(previousValue));
            if (selected) {
                copyOfSelectedMatters.add(id);
            } else if (copyOfSelectedMatters.has(id)) {
                copyOfSelectedMatters.delete(id);
            }

            return copyOfSelectedMatters;
        });
    };

    const handleClientSelectAll = useCallback((matterIds: number[], selected: boolean) => {
        setSelectedMatters((prev) => {
            const next = new Set(prev);
            matterIds.forEach((id) => (selected ? next.add(id) : next.delete(id)));
            return next;
        });
    }, []);

    const resolveMatterErrorReason = (erroredMatter: ErroredMatterDto): string | null => {
        const codeErrorId = erroredMatter.code ? ERROR_CODE_MESSAGES.get(erroredMatter.code) : undefined;
        if (codeErrorId) {
            return t(`error-code-message.${codeErrorId}`);
        }

        return erroredMatter.message;
    };

    const renderSnackbarAlertForEachMatterError = (erroredMatters: ErroredMatterDto[]) => {
        erroredMatters.forEach((erroredMatter) => {
            const matterName = erroredMatter.matterName ?? `${erroredMatter.matterId}`;
            const reason = resolveMatterErrorReason(erroredMatter);
            warningAlert(
                reason
                    ? t('invoices.error-creating-invoice-matter', { matterName, reason })
                    : t('invoices.error-creating-invoice-matter-id', { matterId: `${erroredMatter.matterId}` })
            );
        });
    };

    const renderSnackbarSuccessAlert = (invoiceIds: number[]) => {
        if (!invoiceIds.length) {
            return;
        }

        if (invoiceIds.length === 1) {
            successAlert(t('invoices.single-invoice-draft-created'));
        } else {
            successAlert(t('invoices.number-invoice-drafts-created', { number: `${invoiceIds.length}` }));
        }
    };

    const renderCustomHeader = (fetchFilteredData: (searchText: string) => void, searchText: string) => {
        const handleCreateDraftInvoices = async (dateFrom: string | null, dateTo: string | null) => {
            const data: CreateDraftInvoicesDto = {
                matterIds: Array.from(selectedMatters),
                startDate: dateFrom,
                endDate: dateTo,
            };
            await createDrafts(data)
                .unwrap()
                .then((resp) => {
                    if (resp.createdInvoices.length) {
                        posthog.capture('invoice_created', {
                            type: 'draft',
                            invoice_count: resp.createdInvoices.length,
                        });
                    }
                    fetchFilteredData(searchText);
                    if (resp.createdInvoices.length > 1) {
                        navigate(`/invoices`);
                    } else if (resp.createdInvoices.length === 1) {
                        navigate(`/invoice/${resp.createdInvoices[0]}`);
                    }
                    renderSnackbarSuccessAlert(resp.createdInvoices);
                    renderSnackbarAlertForEachMatterError(resp.erroredMatters);
                })
                .catch((error) => {
                    console.error(error);
                    errorAlert(t('invoices.error-creating-invoices'));
                });
        };

        const handleCreateJointInvoice = async (dateFrom: string | null, dateTo: string | null) => {
            if (selectedMatters.size > 0) {
                if (selectedMattersWithJointInvoiceDisabled.length) {
                    const cases = selectedMattersWithJointInvoiceDisabled.map((item) => item.fullName).join(', ');
                    errorAlert(t('invoices.alerts.joint-invoice-generation-failed', { cases }));
                } else {
                    const data: CreateDraftInvoicesDto = {
                        matterIds: Array.from(selectedMatters),
                        startDate: dateFrom,
                        endDate: dateTo,
                    };
                    await createJointInvoice(data)
                        .unwrap()
                        .then((resp) => {
                            posthog.capture('invoice_created', { type: 'joint' });
                            navigate(`/invoice/${resp.id}`);
                            successAlert(t('invoices.joint-invoice-created'));
                        })
                        .catch((error) => {
                            console.error(error);
                            errorAlert(error?.data?.message ?? t('invoices.error-creating-joint-invoice'));
                        });
                }
            }
        };

        const openDateRangeModal = (onSave: (dateFrom: string, dateTo: string) => void) => {
            openModal(
                <DateRangePickerModal
                    onCancel={closeModal}
                    saveButtonTextId="proceed"
                    maxDateTo={new Date()}
                    atLeastOneRequired
                    showPreviousMonthQuickPick
                    onSave={(dateFrom, dateTo) => {
                        closeModal();
                        onSave(dateFrom, dateTo);
                    }}
                />,
                closeModal
            );
        };

        const invoiceMenuItems: MenuButtonItem[] = [
            {
                label: t('invoices.full-time-range'),
                onClick: () => {
                    void handleCreateDraftInvoices(null, null);
                },
            },
            {
                label: t('invoices.limited-time-range'),
                onClick: () =>
                    openDateRangeModal((dateFrom, dateTo) => {
                        void handleCreateDraftInvoices(dateFrom, dateTo);
                    }),
            },
        ];

        const jointInvoiceMenuItems: MenuButtonItem[] = [
            {
                label: t('invoices.full-time-range'),
                onClick: () => {
                    void handleCreateJointInvoice(null, null);
                },
            },
            {
                label: t('invoices.limited-time-range'),
                onClick: () =>
                    openDateRangeModal((dateFrom, dateTo) => {
                        void handleCreateJointInvoice(dateFrom, dateTo);
                    }),
            },
        ];

        return (
            <PageHeader
                hostsList
                titleId={'billable-clients.billable-clients'}
                showBreadcrumb
                buttons={
                    <div className="flex justify-end gap-2.5">
                        <ListCreateButton
                            permissions={[
                                'CREATE.INVOICES.MATTER_ACCESS',
                                'CREATE.INVOICES.TYPE_PREPAYMENT_WITH_MATTER_ACCESS',
                            ]}
                            variant="outlined"
                            size="small"
                            onClick={openAddNewPrepaidInvoiceModal}
                            disabled={buttonsDisabled}
                            labelId="invoices.new-prepayment"
                            newLabelId="new-entity.prepayment"
                        />
                        <ListCreateButton
                            permissions={[
                                'CREATE.INVOICES.MATTER_ACCESS',
                                'CREATE.INVOICES.TYPE_CREDIT_WITH_MATTER_ACCESS',
                            ]}
                            variant="outlined"
                            size="small"
                            onClick={openAddNewCreditInvoiceModal}
                            disabled={buttonsDisabled}
                            labelId="invoices.new-credit-note"
                            newLabelId="new-entity.credit-note"
                        />
                        <PermissionGuard
                            permissions={[
                                'CREATE.INVOICES.MATTER_ACCESS',
                                'CREATE.INVOICES.TYPE_DEBIT_WITH_MATTER_ACCESS',
                            ]}
                        >
                            {isDateRangeEnabled ? (
                                <MenuButton
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    className={isNewDesign ? NEW_DESIGN_CREATE_CLASS : undefined}
                                    disabled={buttonsDisabled || selectedMatters.size < 1}
                                    menuItems={invoiceMenuItems}
                                >
                                    <FormattedMessage
                                        id={isNewDesign ? 'new-entity.invoice' : 'invoices.new-invoice'}
                                    />
                                </MenuButton>
                            ) : (
                                <Button
                                    {...buttonStyle(
                                        { variant: 'contained', color: 'secondary', size: 'small' },
                                        isNewDesign ? NEW_DESIGN_CREATE_CLASS : undefined
                                    )}
                                    disabled={buttonsDisabled || selectedMatters.size < 1}
                                    onClick={() => {
                                        void handleCreateDraftInvoices(null, null);
                                    }}
                                >
                                    <FormattedMessage
                                        id={isNewDesign ? 'new-entity.invoice' : 'invoices.new-invoice'}
                                    />
                                </Button>
                            )}
                        </PermissionGuard>
                        <PermissionGuard
                            permissions={[
                                'CREATE.INVOICES.MATTER_ACCESS',
                                'CREATE.INVOICES.TYPE_DEBIT_WITH_MATTER_ACCESS',
                            ]}
                        >
                            {isDateRangeEnabled ? (
                                <MenuButton
                                    variant="outlined"
                                    size="small"
                                    className={isNewDesign ? NEW_DESIGN_CREATE_CLASS : undefined}
                                    disabled={buttonsDisabled || selectedMatters.size < 1}
                                    menuItems={jointInvoiceMenuItems}
                                >
                                    <FormattedMessage
                                        id={isNewDesign ? 'new-entity.joint-invoice' : 'invoices.new-joint-invoice'}
                                    />
                                </MenuButton>
                            ) : (
                                <Button
                                    {...buttonStyle(
                                        { variant: 'outlined', size: 'small' },
                                        isNewDesign ? NEW_DESIGN_CREATE_CLASS : undefined
                                    )}
                                    disabled={buttonsDisabled || selectedMatters.size < 1}
                                    onClick={() => {
                                        void handleCreateJointInvoice(null, null);
                                    }}
                                >
                                    <FormattedMessage
                                        id={isNewDesign ? 'new-entity.joint-invoice' : 'invoices.new-joint-invoice'}
                                    />
                                </Button>
                            )}
                        </PermissionGuard>
                    </div>
                }
            />
        );
    };

    return (
        <PaddedLayout className="overflow-hidden">
            {(isLoadingDraftInvoiceCreation || isLoadingJointInvoiceCreation) && <Loader />}
            <div className="flex h-full flex-col gap-4">
                <BillableMattersTable
                    handleMatterSelect={handleMatterSelect}
                    handleClientSelectAll={handleClientSelectAll}
                    renderCustomHeader={renderCustomHeader}
                    setExcludedFromJointInvoicing={setExcludedFromJointInvoicing}
                    selectedIds={selectedMatters}
                    onSelectionChange={setSelectedMatters}
                />
            </div>
        </PaddedLayout>
    );
};

export default BillableMattersPage;
